import { Department } from './index'

export function HasChildrenMatchingSearch(
  department: Department,
  search: string,
): boolean {
  if (!department.children || !search) return false
  return (
    department.children?.some(
      child =>
        child.name.toLowerCase().includes(search) ||
        HasChildrenMatchingSearch(child, search),
    ) ?? false
  )
}

//Recursively checks if any children of a department are selected. Used to check if a department should be expanded
export function HasSelectedChildren(
  department: Department,
  selectedDepartments: Department[],
): boolean {
  if (!department.children || !selectedDepartments) return false
  return (
    department.children?.some(
      child =>
        selectedDepartments.some(dep => dep.id === child.id) ||
        HasSelectedChildren(child, selectedDepartments),
    ) ?? false
  )
}

//Recursively checks if all children of a department are selected
export function AllChildrenSelected(
  children: Department[],
  selected: Department[],
): boolean {
  if (children.length === 0 || selected.length === 0) return false
  return children.every(child =>
    selected.some(
      dep =>
        dep.id === child.id ||
        AllChildrenSelected(child.children || [], selected),
    ),
  )
}

//Recursively gets all children of a department. I.E. Also the children of the children
export function GetAllChildren(departments: Department[]): Department[] {
  let children: Department[] = []
  departments.forEach(dep => {
    if (dep.children) {
      children = children.concat(GetAllChildren(dep.children))
    }
    children.push(dep)
  })
  return children
}

//Adds children to selected without duplicates. "Children" here is a flat array, which can be created using GetAllChildren
export function MergeChildrenWithSelected(
  children: Department[],
  selected: Department[],
): Department[] {
  const uniqueMap = new Map<string, Department>()

  // Add objects from array1 to the map
  children.forEach(child => {
    uniqueMap.set(child.id, child)
  })

  // Add objects from array2 to the map
  selected.forEach(dep => {
    uniqueMap.set(dep.id, dep)
  })

  // Convert the map values back to an array
  const mergedArray = Array.from(uniqueMap.values())

  return mergedArray
}

//Creates the department hierarchy from the flat list of departments returned by the database
export function createDepartmentHierarchy(data: any): any {
  function AppendPathToDepartment(department: Department, currentPath: string) {
    department.path = currentPath
      ? currentPath + '/' + department.name
      : department.name
    if (department.children) {
      department.children.forEach((child: Department) => {
        AppendPathToDepartment(child, department.path)
      })
    }
  }
  function AppendIndexToDepartments(departments: Department[]): Department[] {
    let index = 0
    function AppendIndexToDepartment(department: Department): Department {
      department.index = index
      index += 1
      if (department.children) {
        department.children.forEach(AppendIndexToDepartment)
      }
      return department
    }
    return departments.map(AppendIndexToDepartment)
  }

  if (!data) return []
  const tree: any[] = []
  const childOf: any = {}
  data.forEach(({ node }: any) => {
    const { id, name, parentDepartmentId, hasChildren } = node

    node.key = id
    node.title = name

    if (hasChildren) {
      childOf[id] = childOf[id] || []
      node.children = childOf[id]
    }
    //The parent department may have been filtered out
    const parentIdExistsInDepartments = data.some(
      ({ node }: any) => node.id === parentDepartmentId,
    )
    const parentId = parentIdExistsInDepartments ? parentDepartmentId : null

    parentId
      ? (childOf[parentId] = childOf[parentId] || []).push(node)
      : tree.push(node)
  })
  tree.forEach(child => AppendPathToDepartment(child, ''))
  //IF DEPARTMENTS NEED TO BE SORTED ALPHABETICALLY, DO SO BEFORE THIS POINT
  return AppendIndexToDepartments(tree)
}

//Searches the department hierarchy for departments matching the search parameter.
//If any child matches, the parent is also returned to preserve the hierarchy
export function filterDepartmentsByName(
  data: Department[],
  searchParam: string,
): Department[] | undefined {
  function AppendIndexToDepartments(departments: Department[]): Department[] {
    let index = 0
    function AppendIndexToDepartment(department: Department): Department {
      department.index = index
      index += 1
      if (department.children) {
        department.children.forEach(AppendIndexToDepartment)
      }
      return department
    }
    return departments.map(AppendIndexToDepartment)
  }
  function CheckObject(obj: Department): Department | undefined {
    const nameMatch = obj.name.toLowerCase().includes(searchParam.toLowerCase())
    if (nameMatch) {
      return obj
    } else if (obj.children) {
      const matchingChildren = obj.children
        .map(CheckObject)
        .filter(_ => _)
        .filter((item): item is Department => item !== undefined)
      if (matchingChildren.length > 0) {
        return {
          ...obj,
          children: matchingChildren,
        }
      }
    }

    return undefined
  }
  const filteredData = data
    .map(CheckObject)
    .filter(_ => _)
    .filter((item): item is Department => item !== undefined)
  return AppendIndexToDepartments(filteredData) //filteredData
}

//Uses the tree (createDepartmentHierarchy) variable to return an array of departments based on their id's.
//Requires the tree because the path is not stored in the database, they are only created when the tree is created
export function GetDepartmentsFromIds(
  tree: Department[],
  ids: string[],
): Department[] {
  if (tree.length === 0 || ids.length === 0) return []

  const departments: Department[] = []

  function CheckChildrenForMatchingId(children: Department[]) {
    children.forEach(child => {
      if (ids.includes(child.id)) {
        departments.push(child)
      }
      if (child.children) {
        CheckChildrenForMatchingId(child.children)
      }
    })
  }
  tree.forEach(dep => {
    if (ids.includes(dep.id)) {
      departments.push(dep)
    }
    if (dep.children) {
      CheckChildrenForMatchingId(dep.children)
    }
  })
  return departments
}

export function GetDepartmentFromIndex(
  tree: Department[],
  index: number,
): Department | undefined {
  if (tree.length === 0 || typeof index !== 'number') return undefined

  const allDepartments = GetAllChildren(tree)
  return allDepartments.find(dep => dep.index === index) || undefined
}
